import { cn } from '@/lib/cn';

/**
 * Cross-entity status pill.
 *
 * Renders a colour-coded dot + label. When the status is "in-progress"
 * (crawling, drafting, …) the dot pulses to convey live activity.
 */

const IN_PROGRESS = new Set([
  'queued',
  'discovering',
  'crawling',
  'checking',
  'scoring',
  'narrating',
  'rendering',
  'pulling',
  'analyzing',
  'researching',
  'drafting',
  'delivering',
  'expanding',
  'enriching',
  'classifying',
  'clustering',
  // A5 v2 competitor content analysis states
  'fetching',
  'extracting',
  'embedding',
  'synthesizing',
  'critiquing',
]);

type Tone = 'success' | 'danger' | 'warn' | 'info' | 'muted';

const TONE: Record<string, Tone> = {
  // success / terminal
  completed: 'success',
  delivered: 'success',
  // failure
  failed: 'danger',
  // warning
  needs_review: 'warn',
  cancelled: 'muted',
  queued: 'muted',
};

const STYLES: Record<Tone, { wrap: string; dot: string }> = {
  success: {
    wrap: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  danger: {
    wrap: 'border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400',
    dot: 'bg-red-500',
  },
  warn: {
    wrap: 'border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
  info: {
    wrap: 'border-sky-500/20 bg-sky-500/10 text-sky-600 dark:text-sky-400',
    dot: 'bg-sky-500',
  },
  muted: {
    wrap: 'border-border bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/60',
  },
};

/**
 * User-friendly copy for every FSM status across all agents.
 *
 * Keep in sync with `packages/db/src/state.ts` — but note that the wire
 * format (enum values) stays as-is; this map only affects human display.
 */
export const STATUS_LABELS: Record<string, string> = {
  // shared / lifecycle
  queued: 'Queued',
  completed: 'Completed',
  delivered: 'Delivered',
  failed: 'Failed',
  cancelled: 'Cancelled',
  needs_review: 'Needs your review',
  rendering: 'Rendering',
  narrating: 'Writing summary',
  scoring: 'Scoring',
  enriching: 'Enriching data',
  classifying: 'Classifying',
  clustering: 'Clustering',
  analyzing: 'Analyzing',
  planning: 'Planning',

  // A1 Audit
  discovering: 'Discovering SERP',
  crawling: 'Crawling site',
  checking: 'Running checks',

  // A2 Keyword
  expanding: 'Expanding keywords',

  // A3 Brief
  researching: 'Researching SERP',
  drafting: 'Drafting',
  delivering: 'Finalizing',

  // A4 Report
  pulling: 'Loading data',

  // A5 v1 Competitor
  sitemap_fetching: 'Loading sitemaps',
  rank_checking: 'Checking rankings',
  backlink_checking: 'Checking backlinks',
  digesting: 'Composing digest',

  // A5 v2 Competitor content analysis
  fetching: 'Fetching pages',
  extracting: 'Extracting content',
  embedding: 'Embedding',
  synthesizing: 'Synthesizing',
  critiquing: 'Polishing',

  // A6 Calendar
  inventorying: 'Cataloging content',
  mapping_coverage: 'Mapping coverage',
  identifying_pillars: 'Identifying pillars',
  sequencing: 'Scheduling',

  // Campaign
  parsing_goal: 'Reading goal',
  decomposing: 'Breaking down goal',
  awaiting_approval: 'Awaiting approval',
  executing: 'Executing',
  evaluating: 'Evaluating',
  replanning: 'Adjusting plan',

  // Forecast
  loading_history: 'Loading history',
  baselining: 'Establishing baseline',
  modeling_impact: 'Modeling impact',
  scenarios: 'Building scenarios',
  composing: 'Composing report',
};

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

function toneFor(status: string): Tone {
  if (TONE[status]) return TONE[status];
  if (IN_PROGRESS.has(status)) return 'info';
  return 'muted';
}

function prettify(status: string): string {
  return STATUS_LABELS[status] ?? capitalize(status.replace(/_/g, ' '));
}

/**
 * Convert a raw FSM status enum value into user-friendly copy.
 * Safe to use anywhere raw statuses would otherwise be rendered.
 */
export function humanStatus(status: string | null | undefined): string {
  if (!status) return '';
  return prettify(status);
}

export interface StatusPillProps {
  status: string;
  className?: string;
}

export function StatusPill({ status, className }: StatusPillProps): JSX.Element {
  const tone = toneFor(status);
  const style = STYLES[tone];
  const pulse = IN_PROGRESS.has(status);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium tracking-wide',
        style.wrap,
        className,
      )}
    >
      <span className="relative inline-flex h-1.5 w-1.5">
        {pulse ? (
          <span
            className={cn(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
              style.dot,
            )}
          />
        ) : null}
        <span className={cn('relative inline-flex h-1.5 w-1.5 rounded-full', style.dot)} />
      </span>
      <span>{prettify(status)}</span>
    </span>
  );
}
